import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { PageLayout } from '@/components/layout/PageLayout'
import TitleSection from '@/components/content/TitleSection'
import Button from '@/components/content/Button'
import { HStack, VStack } from '@musinsa/mwms-ds'
import ShipperForm, { INIT_FORM } from './components/ShipperForm'
import ShipperAddModal from './components/ShipperAddModal'
import GridTable from '@/components/table/GridTable'
import { AgGridReact } from 'ag-grid-react'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { ShipperItem, GetShipperListV2Req } from '@/services/shipper/types'
import { GET_SHIPPER_LIST_V2, useGetShipperListV2 } from '@/services/shipper/useShipperQueries'
import { flushSync } from 'react-dom'
import { PAGE_SIZE_ON_SHIPPER, SHIPPER_TYPES } from '@/services/shipper/apis'
import { useForm, FormProvider } from 'react-hook-form'
import useModal from '@/hooks/useModal'
import { queryClient } from '@/utils/queryClient'
import ShipperUpdateModal from './components/ShipperUpdateModal'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { omitBy } from 'lodash-es'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useShipperFieldSelection } from '@/components/system/ShipperSelector'
import { useCompanyIdSpecialChars } from './components/hooks/useCompanyIdSpecialChars'

const getInitForm = (parsedQs: Partial<GetShipperListV2Req> = {}): Partial<GetShipperListV2Req> => ({
  shipperNames: parsedQs.shipperNames || [],
  logisticsServiceTypes: parsedQs.logisticsServiceTypes
    ? Array.isArray(parsedQs.logisticsServiceTypes)
      ? parsedQs.logisticsServiceTypes
      : [parsedQs.logisticsServiceTypes]
    : SHIPPER_TYPES,
})

function ShipperPage() {
  const { reload } = useMenuOnSession()

  // 업체아이디 특수문자 게이트(LOGISPRD-11899)를 미리 캐시에 올려 둔다.
  // 추가·수정 모달이 열리는 시점에는 값이 정해져 있어야 검증 규칙이 도중에 바뀌지 않는다.
  useCompanyIdSpecialChars()

  const $grid = useRef<AgGridReact>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const { open, onOpenModal, onCloseModal } = useModal()
  const { parsedQs, replaceQuery } = useParsedQuery<GetShipperListV2Req & { isShipperNamesSelectedAll?: boolean }>()

  // MEMO: 화주사 아이템이 선택되면 수정 모달 열기
  const [selectedShipperData, setSelectedShipperData] = useState<ShipperItem | null>(null)
  const isUpdateModalOpen = selectedShipperData !== null

  // Form methods
  const methods = useForm<GetShipperListV2Req>({
    defaultValues: getInitForm(parsedQs),
  })

  const [searchParams, setSearchParams] = useState<GetShipperListV2Req>(INIT_FORM)

  const { isAllSelected, handleShipperFieldChange, resetShipperSelection, processShipperDataForSubmit } =
    useShipperFieldSelection<GetShipperListV2Req>({
      logisticsServiceTypes: parsedQs?.logisticsServiceTypes?.length === SHIPPER_TYPES.length,
      shipperNames: String(parsedQs?.isShipperNamesSelectedAll) === 'true',
    })

  const ButtonCellRenderer = useCallback((params: ICellRendererParams<ShipperItem, string>) => {
    if (params.data?.logisticsServiceType === 'M1P') {
      return null
    }

    return (
      <Button
        kind="secondary"
        fit
        size="sm"
        style={{ margin: '28px 0px' }}
        onClick={() => {
          setSelectedShipperData(params.data || null)
        }}
      >
        수정
      </Button>
    )
  }, [])

  const ArrayCellRenderer = useCallback((params: ICellRendererParams<ShipperItem, string>) => {
    const columnKey = params?.column?.getColId()?.split('.')

    if (!columnKey) {
      return null
    }

    const arrayData = params?.data?.[columnKey[0] as keyof ShipperItem] || []

    if (!Array.isArray(arrayData)) {
      return null
    }

    return (
      <div style={{ height: '100%', padding: '12px 8px' }}>
        {arrayData.map((item: ShipperItem['salesPlatforms'][number], index: number) => {
          const cellValue = item[columnKey[1] as keyof ShipperItem['salesPlatforms'][number]]
          if (typeof cellValue === 'boolean') {
            return <div key={index}>{cellValue ? 'Y' : 'N'}</div>
          }
          return <div key={index}>{cellValue}</div>
        })}
      </div>
    )
  }, [])
  const { data, refetch, isFetching } = useGetShipperListV2({
    shipperNames: searchParams.shipperNames,
    logisticsServiceTypes: searchParams.logisticsServiceTypes,
    page: currentPage,
  })

  const columnDefs = useMemo<ColDef<any>[]>(() => {
    return [
      { colId: 'edit', headerName: '수정', width: 110, cellRenderer: ButtonCellRenderer, cellStyle: { display: 'flex', alignItems: 'center' } },
      {
        field: 'logisticsServiceType',
        headerName: '화주유형',
        width: 100,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'shipperCode',
        headerName: '화주코드',
        width: 140,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'shipperName',
        headerName: '화주명',
        width: 180,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'companyName',
        headerName: '상호명',
        width: 180,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'ownerName',
        headerName: '대표자명',
        width: 140,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'businessRegistrationNumber',
        headerName: '사업자등록번호',
        width: 140,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'address',
        headerName: '사업자 주소',
        width: 300,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'phoneNumber',
        headerName: '전화번호',
        width: 180,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'businessType',
        headerName: '업태',
        width: 180,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'businessMajor',
        headerName: '업종',
        width: 180,
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'mfsTermsAgreedAt',
        headerName: '약관동의 수집일시',
        width: 180,
        cellDataType: 'datetime',
        cellStyle: { display: 'flex', alignItems: 'center' },
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'salesPlatforms.companyId',
        headerName: '플랫폼(업체아이디)',
        width: 180,
        autoHeight: true,
        cellRenderer: ArrayCellRenderer,
        valueFormatter: (params) => params.value || '-',
      },
      {
        field: 'salesPlatforms.usable',
        headerName: '주문수집 여부',
        width: 120,
        autoHeight: true,
        cellRenderer: ArrayCellRenderer,
        valueFormatter: (params) => params.value || '-',
      },
    ]
  }, [])

  const onChangePage = useCallback(
    async (page: number) => {
      flushSync(() => setCurrentPage(page))
      await refetch()
    },
    [refetch],
  )

  const handleReset = useCallback(() => {
    methods.reset(INIT_FORM)
    setCurrentPage(0)
    resetShipperSelection()
  }, [methods, resetShipperSelection])

  const handleSearch = useCallback(
    async (formData: GetShipperListV2Req) => {
      const processedData = processShipperDataForSubmit(formData)
      const query = omitBy(processedData, (value) => {
        if (Array.isArray(value)) {
          return value.length === 0
        }
        return value === ''
      }) as GetShipperListV2Req

      MenuStorage.menuOnSession.setSearch({ ...formData, isShipperNamesSelectedAll: isAllSelected.shipperNames })

      flushSync(() => {
        setSearchParams(query)
        setCurrentPage(0)
      })

      reload()

      await refetch()
      replaceQuery({ ...formData, isShipperNamesSelectedAll: isAllSelected.shipperNames })
    },
    [refetch, replaceQuery, processShipperDataForSubmit, reload],
  )

  const handleFieldChange = useCallback(
    ({ fieldName, value, isSelectedAll }: { fieldName?: keyof GetShipperListV2Req; value?: unknown; isSelectedAll?: boolean }) => {
      // ShipperSelector에서 호출된 경우 isSelectedAll 처리
      if (isSelectedAll !== undefined && fieldName) {
        handleShipperFieldChange({ fieldName, value, isSelectedAll })
      }

      if (fieldName) {
        const currentFormData = methods.getValues()
        const newSearchParams = {
          ...currentFormData,
          [fieldName]: value,
        }
        setCurrentPage(0)
      }
    },
    [methods, handleShipperFieldChange],
  )

  const handleAddRow = useCallback(() => {
    onOpenModal()
  }, [])

  const handleCloseAddModal = useCallback(() => {
    onCloseModal()
  }, [])

  const handleRefreshAfterAdd = useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: GET_SHIPPER_LIST_V2,
    })
    await refetch()
  }, [refetch])

  const handleCloseUpdateModal = useCallback(() => {
    onCloseModal()
    setSelectedShipperData(null)
  }, [])

  const handleRefreshAfterUpdate = useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: GET_SHIPPER_LIST_V2,
    })

    await refetch()
  }, [refetch])

  return (
    <PageLayout>
      <FormProvider {...methods}>
        <VStack gap={12}>
          <TitleSection variant="legacy" title="화주사 관리" />
          <ShipperForm
            isLoading={isFetching}
            onReset={handleReset}
            onSearch={methods.handleSubmit(handleSearch)}
            onFieldChange={handleFieldChange}
          />
        </VStack>
        <HStack full>
          <GridTable<ShipperItem>
            gridKey="MA14_HEAD"
            ref={$grid}
            isLoading={isFetching}
            countPerPage={PAGE_SIZE_ON_SHIPPER}
            totalCount={data.totalCount}
            totalPages={data.totalPages}
            currentPage={currentPage}
            rowData={data.list}
            columnDefs={columnDefs}
            addButtonName="화주사 추가"
            onChangePage={onChangePage}
            onAddRow={handleAddRow}
          />
        </HStack>

        <ShipperAddModal open={open} onClose={handleCloseAddModal} onRefresh={handleRefreshAfterAdd} />
        {selectedShipperData && (
          <ShipperUpdateModal
            open={isUpdateModalOpen}
            shipperData={selectedShipperData}
            onClose={handleCloseUpdateModal}
            onRefresh={handleRefreshAfterUpdate}
          />
        )}
      </FormProvider>
    </PageLayout>
  )
}

export default ShipperPage
